import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { RowActionButton } from '@/components/common/RowActions';
import { HighlightText } from '@/components/common/HighlightText';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/date';
import { FEEDBACK_STATUS_TONE, FEEDBACK_STATUS_LABELS } from '../constants/feedback.constants';
import type { FeedbackListItem } from '../types/feedback.types';

interface FeedbackColumnActions {
  onView: (feedback: FeedbackListItem) => void;
}

interface GetFeedbackColumnsArgs {
  searchText?: string | undefined;
  actions?: FeedbackColumnActions | undefined;
}

export function getFeedbackColumns({
  searchText,
  actions,
}: GetFeedbackColumnsArgs = {}): ColumnDef<FeedbackListItem>[] {
  return [
    createSerialColumn<FeedbackListItem>(),
    {
      id: 'user',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original.user;
        if (!user) return <EmptyValue />;
        const isUnread = row.original.readAt === null;
        return (
          <StackedCellText
            primary={user.fullName}
            primaryClassName={isUnread ? 'font-bold' : 'font-medium'}
            secondary={user.hrEmpId}
          />
        );
      },
    },
    {
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => {
        const isUnread = row.original.readAt === null;
        return (
          <WrappingCellText width="wide" className={isUnread ? 'font-semibold' : undefined}>
            <HighlightText text={row.original.message} highlightedText={searchText} />
          </WrappingCellText>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.original.type;
        if (!type) return <EmptyValue />;
        return (
          <Badge variant="outline" className="font-normal">
            {type.name}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <StatusBadge tone={FEEDBACK_STATUS_TONE[status]}>
            {FEEDBACK_STATUS_LABELS[status]}
          </StatusBadge>
        );
      },
    },
    {
      accessorKey: 'feedbackDate',
      header: 'Feedback Date',
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">
          {formatDisplayDate(row.original.feedbackDate)}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Submitted',
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">
          {formatDisplayDateTime(row.original.createdAt)}
        </span>
      ),
    },
    ...(actions
      ? [
          createActionsColumn<FeedbackListItem>((feedback) => (
            <RowActionButton
              label="View"
              ariaLabel={
                feedback.user ? `View feedback from ${feedback.user.fullName}` : 'View feedback'
              }
              onClick={() => actions.onView(feedback)}
            />
          )),
        ]
      : []),
  ];
}
